import { reset, execute, renderMultipleCaptchasOnSamePage, renderV2WhenReady } from './captcha-google.js';
import {fetchCsrfToken} from '../csrf-token-module.js';
import {doFetch} from './fetch.js';
import InputValidator from './InputValidator.js';
import * as loadingMask from './load-mask.js';

/**
 * @file This file collects all js code related to the login popup and the login page.
 *       It also contains the code for the password reset and the customer registration.
 * TODO Move all login code to this file.
 * TODO separate login from reset password logic and registration logic or rename file
 * TODO extract functions which are unrelated to login
 */

window.addEventListener('load', function() {
  window.recaptchaIds = [];

  addPageListener(document.getElementById('js-login-container').dataset.action);

  // Reload the page when the user navigates with the browser back/forward buttons,
  // so the server renders the correct login step for the current URL.
  if (!document.getElementById('js-checkout-login-data')) {
    window.addEventListener('popstate', function() {
      location.reload();
    });
  }
});

const DEFAULT_ERROR = 'Es ist leider ein Fehler aufgetreten. Bitte versuche es später erneut.';

function addLoginFormListenersAndAutofocusPassword() {
  const loginForm = document.getElementById('js-login-form');
  /** @type {HTMLInputElement} */
  const passwordInput = loginForm.querySelector('input[type="password"]');
  const capsError = loginForm.querySelector('.pw_caps_error');
  passwordInput.addEventListener('keyup', (event) => {checkCapsLock(event, capsError);});
  passwordInput.addEventListener('keydown', (event) => {checkCapsLock(event, capsError);});
  passwordInput.addEventListener('blur', () => {capsError.style.display = 'none';});

  const loginEye = loginForm.querySelector('.js_login_eye');
  loginEye.addEventListener('click', function() {
    togglePasswordVisibility(passwordInput, loginEye);
  });

  loginForm.addEventListener('submit', function(event) {
    event.preventDefault();
    login(this);
  });

  passwordInput.focus();
}

/**
 * Checks the state of the caps lock key and displays an error message if it is active.
 *
 * @param {Event} e
 * @param {HTMLElement} capsError
 */
function checkCapsLock(e, capsError) {
  if (!(e instanceof KeyboardEvent)) {
    return;
  }

  if (e.getModifierState('CapsLock')) {
    capsError.style.display = 'block';
  } else {
    capsError.style.display = 'none';
  }
}

/**
 * Toggle the visibility of the password in the given input.
 *
 * @param {HTMLInputElement} passwordInput
 * @param {HTMLElement} loginEye
 */
function togglePasswordVisibility(passwordInput, loginEye) {
  const isHidden = passwordInput.type === 'password';
  passwordInput.type = isHidden ? 'text' : 'password';

  // toggle icons
  loginEye.querySelector('.eye_open').style.display = isHidden ? 'none' : 'block';
  loginEye.querySelector('.eye_closed').style.display = isHidden ? 'block' : 'none';

  // update accessible label + pressed state
  loginEye.setAttribute('aria-label', isHidden ? 'Passwort ausblenden' : 'Passwort anzeigen');
  loginEye.setAttribute('aria-pressed', String(isHidden));
}

function addPageListener(action) {
  switch (action) {
    case 'start':
      addCheckEmailListener();
      addThirdPartyLoginListener();
      break;

    // Login cases
    case 'login':
      addLoginFormListenersAndAutofocusPassword();
      addEmailLoginListener();
      addChangeViewListener();
      break;
    case 'login_external':
    case 'login_no_password':
      addEmailLoginListener();
      addThirdPartyLoginListener();
      break;
    case 'login_reset_password':
      addPwRequestListener();
      break;

    // Register cases
    case 'register_start':
      addChangeViewListener();
      break;
    case 'register_new_customer':
      addNewCustomerListener();
      break;
    case 'register_marketplace_finish':
      addMarketplaceRegistrationListener();
      break;
    case 'register_new_customer_marketplace':
      addChangeViewListener();
      break;
    case 'register_marketplace_imei':
      addChangeViewListener();
      initMarketplaceIdentifyForm();
      break;
    case 'register_marketplace_sms_confirm':
      addChangeViewListener();
      addMarketplaceSendSmsListener();
      break;
    case 'register_marketplace_enter_code':
      addChangeViewListener();
      initMarketplaceCodeForm();
      break;
    default:
      console.error('Unknown action:', action);
  }
}

function addPwRequestListener() {
  const emailInput = document.getElementById('js-email');
  const validator = new InputValidator(emailInput);

  emailInput.addEventListener('change', function() {
    validator.validate(InputValidator.email, InputValidator.defaultMessages.email);
  });

  const form = document.getElementById('js-reset-password-form');
  form.addEventListener('submit', function(event) {
    event.preventDefault();
    if (validator.validate(InputValidator.email, InputValidator.defaultMessages.email)) {
      resetPasswordRequest(this);
    } else {
      InputValidator.focusFirstInvalid(form);
    }
  });
}

/**
 * Marketplace: identify customer by IMEI or invoice without page reload.
 * Intercepts the submit of the form on registerMarketplaceImei.html.twig
 */
function initMarketplaceIdentifyForm() {
  const form = document.querySelector('.js_find_order_form');
  if (!form) {
    return;
  }

  form.addEventListener('submit', (e) => {
    e.preventDefault();
    hideErrorMessages();

    /** @type {HTMLInputElement|null} */
    const input = form.querySelector('input[name="orderIdentifier"]');
    const val = (input && typeof input.value === 'string') ? input.value.trim() : '';
    if (!val) {
      setErrorMessage(form, 'Bitte gib eine IMEI oder Rechnungsnummer ein.');
      return;
    }

    // Simple heuristic: 14+ digits (or exactly 15) => IMEI, else invoice
    const isImei = /^\d{14,}$/.test(val);

    const formdata = new FormData();
    formdata.append('identifierType', isImei ? 'imei' : 'invoice');
    formdata.append('identifier', val);

    loadingMask.show();
    doFetch('/loginActionsEndpoint.php?action=marketplaceIdentify', {
      method: 'POST',
      body: formdata,
    }, (response) => {
      if (response?.success && response?.data?.html) {
        showResponseHtml(response.data);
      } else {
        setErrorMessage(form, response?.error || DEFAULT_ERROR);
      }
      loadingMask.hide();
    });
  });
}

function addChangeViewListener() {
  const changeViewButtons = document.querySelectorAll('.js_change_view_button');
  const loginContainer = document.getElementById('js-login-container');
  const loginCheckoutContainer = document.getElementById('js-checkout-login-data');

  changeViewButtons.forEach(button => {
    button.addEventListener('click', (event) => {
      event.preventDefault(); // Prevent the default link navigation

      const action = button.dataset.targetAction;
      const targetUrl = button.getAttribute('href');

      if (!action) {
        console.error('Button is missing the data-target-action attribute:', button);
        return;
      }

      doFetch('/loginActionsEndpoint.php?action=renderLoginView&actionTarget=' + action,
          null,
          (response) => {
        if (response && response.data.html && typeof response.data.html === 'string') {
          loginContainer.innerHTML = response.data.html;
          loginContainer.dataset.action = action;

          // Update the browser's URL and title without a full page reload
          if (!loginCheckoutContainer) {
            if (targetUrl) {
              history.pushState({ action }, '', targetUrl);
            }
            if (response.data.title) {
              document.title = response.data.title;
            }
          }

          addPageListener(action);
        } else {
          console.error('Invalid response from the server:', response);
          loginContainer.innerHTML = '<p class="login_error" style="display: block;">Ein Fehler ist aufgetreten. Bitte versuche es erneut und kontaktiere ansonsten den <a href="/hilfe">Kundenservice</a>.</p>';
        }
      });
    });
  });
}


/**
 * Reset all recaptcha v2 challenges.
 *
 * This is necessary because we don't always reload the page after an
 * ajax call, so the user might need to solve another captcha.
 */
function resetAllV2Captchas() {
  for (const recaptchaId of window.recaptchaIds) {
    reset(recaptchaId);
  }
}

/**
 * Fetches the given url after processing the captcha data.
 *
 * @param {HTMLFormElement} form
 * @param {string} url
 * @param onSuccess
 * @param onFail
 * @param {string} recaptchaAction
 */
function fetchWithCaptcha(form, url, onSuccess, onFail, recaptchaAction) {
  hideErrorMessages();
  let formData = new FormData(form);

  // Check if a recaptcha v2 has been solved
  if (formData.has('g-recaptcha-response')) {
    fetchWithCaptchaV2(form, url, formData, onSuccess, onFail);
  } else {
    // If no recaptcha v2 has been solved, use the invisible recaptcha v3
    fetchWithCaptchaV3(form, url, formData, onSuccess, onFail, recaptchaAction);
  }
}

/**
 * Fetches the given url after processing the captcha data.
 * This function is used for recaptcha v2.
 *
 * @param {HTMLFormElement} form
 * @param {string} url
 * @param {FormData} formData
 * @param onSuccess
 * @param onFail
 */
function fetchWithCaptchaV2(form, url, formData, onSuccess, onFail) {
  loadingMask.show();
  fetch(url, {
    method: 'post',
    body: formData,
    headers: {'X-CSRF-Token': fetchCsrfToken()},
  }).then((rawResponse) => {
    if (!rawResponse.ok) {
      loadingMask.hide();
      setErrorMessage(form, DEFAULT_ERROR);
      return;
    }

    rawResponse.json().then((response) => {
      resetAllV2Captchas();
      if (response.success) {
        onSuccess(response);
      } else {
        onFail(response);
      }
    });
  });
}

/**
 * @param {HTMLFormElement} form
 * @param {Request | string | URL} url
 * @param {FormData} formData
 * @param onSuccess
 * @param onFail
 * @param {string} recaptchaAction
 */
function fetchWithCaptchaV3(form, url, formData, onSuccess, onFail, recaptchaAction) {
  loadingMask.show();
  execute(recaptchaAction, function(token) {
    formData.append('recaptchaResponse', token);

    fetch(url, {
      method: 'post',
      body: formData,
      headers: {'X-CSRF-Token': fetchCsrfToken()},
    }).then((rawResponse) => {
      if (!rawResponse.ok) {
        loadingMask.hide();
        setErrorMessage(form, DEFAULT_ERROR);
        return;
      }

      rawResponse.json().then((response) => {
        if (response.success) {
          onSuccess(response);
        } else if (response.error === 'captcha_failed') {
          setErrorMessage(form, 'Bitte bestätige, dass du kein Roboter bist.');
          renderRecaptchaChallenge(form);
        } else {
          onFail(response);
        }
      });
    });
  });
}

function resetPasswordRequest(resetPasswordForm) {
  loadingMask.show();
  fetchWithCaptcha(
      resetPasswordForm,
      '/loginActionsEndpoint.php?action=requestPasswordReset',
      (response) => {
        showSuccessMessage(response.data.successMessage);
        const button = resetPasswordForm.querySelector('button[type="submit"]');
        if (button) {
          button.disabled = true;
        }
        loadingMask.hide();
      },
      (response) => {
        setErrorMessage(resetPasswordForm, response.error);
        loadingMask.hide();
      },
      'resetPassword',
  );
}

function login(loginForm) {
  loadingMask.show();
  fetchWithCaptcha(
      loginForm,
      '/loginActionsEndpoint.php?action=login',
      (response) => fetchAndRedirect(response.data.target),
      (response) => {
        setErrorMessage(loginForm, response.error);
        loadingMask.hide();
      },
      'login'
  );
}

/**
 * Renders the recaptcha v2 challenge in the given form
 *
 * @param {HTMLFormElement} form
 */
function renderRecaptchaChallenge(form) {
  const challengeElement = form.querySelector('.js_recaptcha_v2_challenge');

  // If there is no element to render the challenge in just return
  if (challengeElement === null) {
    return;
  }

  renderMultipleCaptchasOnSamePage(
      form.querySelector('.js_recaptcha_v2_challenge'));
}

/**
 * Shows an error message in the given form
 *
 * @param {HTMLFormElement} form
 * @param {string} message
 */
function setErrorMessage(form, message) {
  const messageElement = form.querySelector('.login_error');
  messageElement.innerHTML = message;
  messageElement.style.display = 'block';
}

function hideErrorMessages() {
  document.querySelectorAll('.login_error').forEach((el) => {
    el.style.display = 'none';
  });
}

export function submitPwForgotRequest(event) {
  loadingMask.show();
  event.preventDefault();
  const formData = new FormData(event.target);
  const action = 'requestPasswordResetByCustomerId';

  // Check if a recaptcha v2 has been solved
  if (formData.has('g-recaptcha-response')) {
    submitPwForgotRequestv2(formData, action);
  } else {
    // If no recaptcha v2 has been solved, use the invisible recaptcha v3
    submitPwForgotRequestv3(formData, action);
  }
}

function submitPwForgotRequestv3(formData, action) {
  execute(action, function(token) {
    formData.append('recaptchaResponse', token);

    fetch('/loginActionsEndpoint.php?action=' + action, {
      method: 'POST',
      body: formData,
      headers: {'X-CSRF-Token': fetchCsrfToken()},
    }).then((res) => {
      res.json().then((data) => {
        const errorElement = document.querySelector('.login_postcode_error');
        if (data.success) {
          errorElement.innerHTML = 'Den Login-Link haben wir an folgende E-Mail-Adresse versendet: ' +
              data.data.email;
        } else if (data.error === 'captcha_failed') {
          errorElement.innerHTML = 'Bitte bestätige, dass du kein Roboter bist.';
          const challengeElement = document.querySelector(
              '.js_ruecksende_recaptcha_v2_challenge');

          renderV2WhenReady(challengeElement);
        } else {
          errorElement.innerHTML = data.error;
        }
        loadingMask.hide();
      });
    });
  });
}

function submitPwForgotRequestv2(formData, action) {
  fetch('/loginActionsEndpoint.php?action=' + action, {
    method: 'POST',
    body: formData,
    headers: {'X-CSRF-Token': fetchCsrfToken()},
  }).then((res) => {
    res.json().then((data) => {
      const errorElement = document.querySelector('.login_postcode_error');
      if (data.success) {
        errorElement.innerHTML = 'Den Login-Link haben wir an folgende E-Mail-Adresse versendet: ' +
            data.email;
      } else {
        errorElement.innerHTML = data.error;
      }
      loadingMask.hide();
    });
  });
}

function addThirdPartyLoginListener() {
  document.querySelectorAll('.js_third_party_login').forEach(function(el) {
    el.addEventListener('click', function() {
      redirectToThirdPartyProvider(el.dataset.provider,  el.dataset.target);
    });
  });
}

function redirectToThirdPartyProvider(providerName, target) {
  let formData = new FormData();
  formData.append('provider', providerName);
  formData.append('target', target);

  fetch('/loginActionsEndpoint.php?action=redirectToThirdPartyProvider', {
    method: 'POST',
    body: formData,
    headers: {'X-CSRF-Token': fetchCsrfToken()},
  }).then((res) => {
    res.json().then((data) => {
      if (data.success) {
        window.location = data.data.url;
      } else {
        setErrorMessage(document.getElementById('js-login-form'), data.error);
      }
    });
  });
}

function addEmailLoginListener() {
  document.getElementById('js-login-one-time-link').addEventListener('click', function() {
    let email = document.getElementById('js-login-form-email').value;
    if (email === '') {
      setErrorMessage(document.getElementById('js-login-form'), 'Bitte gib eine E-Mail-Adresse ein.');
      return;
    }
    loadingMask.show();
    hideErrorMessages();

    let target = document.getElementById('js-login-one-time-link').dataset.target;

    let formData = new FormData();
    formData.append('linkrequestmail', email);
    doFetch('/shopController.php?objective=requestLink', {
      method: 'POST',
      body: formData,
    }, function(res) {
      if (res.success) {
        document.getElementById('js-login-one-time-link-wrapper').innerHTML = res.successMessage;
        startVerificationBgCheck(target);
      } else {
        setErrorMessage(document.getElementById('js-login-form'), res.error);
      }

      loadingMask.hide();
    });
  });
}

function startVerificationBgCheck(target) {
  const intervalId = window.setInterval(function() {
    doFetch('/shopController.php?objective=checkVerification', {}, function(res) {
      if (res) {
        if (res.success) {
          window.clearInterval(intervalId);
          if (target) {
            location.href = target;
          } else {
            location.reload();
          }
        }
      }
    });
  }, 6000);
}

function addCheckEmailListener() {
  const emailInput = document.getElementById('js-login-form-email');
  const validator = new InputValidator(emailInput);
  const form = document.getElementById('js-login-email-form');

  emailInput.addEventListener('change', function() {
    validator.validate(InputValidator.email, InputValidator.defaultMessages.email);
    const button = form.querySelector('button[type="submit"]');
    button.disabled = !InputValidator.email(this.value);
  });

  form.addEventListener('submit', (event) => {
    event.preventDefault();
    if (validator.validate(InputValidator.email, InputValidator.defaultMessages.email)) {
      let url = '/loginActionsEndpoint.php?action=checkEmailUsage';
      let checkoutData = document.getElementById('js-checkout-login-data');
      if (checkoutData) {
        url += '&checkoutType=' + checkoutData.dataset.currentCheckoutType;
      }

      fetchWithCaptcha(
          form,
          url,
          (response) => {
            showResponseHtml(response.data);
            loadingMask.hide();
          },
          (response) => {
            setErrorMessage(form, response.error);
            loadingMask.hide();
          },
          'checkEmailUsage'
      );
    } else {
      InputValidator.focusFirstInvalid(form);
    }
  });

  emailInput.addEventListener('keyup', function() {
    const button = form.querySelector('button[type="submit"]');
    button.disabled = !InputValidator.email(this.value);
  });
}

function showResponseHtml(response) {
  const loginContainer = document.getElementById('js-login-container');
  loginContainer.innerHTML = response.html;
  loginContainer.dataset.action = response.step;

  if (response.title) {
    document.title = response.title;
  }

  const loginCheckoutContainer = document.getElementById('js-checkout-login-data');
  if (!loginCheckoutContainer) {
    history.pushState({ action: response.step }, '', '/login?action=' + response.step);
  }

  addPageListener(response.step);
}

function showSuccessMessage(message) {
  document.getElementById('js-login-success-container').innerHTML = message;
}


function addNewCustomerListener() {
  const firstNameInput = document.getElementById('js-firstname');
  const firstNameValidator = new InputValidator(firstNameInput);
  firstNameInput.addEventListener('change', function() {
    firstNameValidator.validate(InputValidator.required, InputValidator.defaultMessages.required);
  });

  const lastNameInput = document.getElementById('js-lastname');
  const lastNameValidator = new InputValidator(lastNameInput);
  lastNameInput.addEventListener('change', function() {
    lastNameValidator.validate(InputValidator.required, InputValidator.defaultMessages.required);
  });

  const addPasswordLink = document.getElementById('js-add-password-link');
  addPasswordLink.addEventListener('click', function() {
    document.getElementById('js-password-area').classList.remove('hidden');
    addPasswordLink.style.display = 'none';
  });

  const passwordInput = document.getElementById('js-password');
  const passwordValidator = new InputValidator(passwordInput);
  passwordInput.addEventListener('change', function() {
    passwordValidator.validate(InputValidator.password, InputValidator.defaultMessages.password);
  });

  const form = document.getElementById('js-new-customer-form');
  form.addEventListener('submit', function(event) {
    event.preventDefault();
    const isFirstNameValid = firstNameValidator.validate(InputValidator.required, InputValidator.defaultMessages.required);
    const isLastNameValid = lastNameValidator.validate(InputValidator.required, InputValidator.defaultMessages.required);
    const isPasswordValid = !passwordInput.value || passwordValidator.validate(InputValidator.password, InputValidator.defaultMessages.password);

    if (isFirstNameValid && isLastNameValid && isPasswordValid) {
      loadingMask.show();
      registerNewCustomer(this);
    } else {
      InputValidator.focusFirstInvalid(form);
    }
  });
}

/**
 * Marketplace: dedicated listener for the final registration step.
 */
function addMarketplaceRegistrationListener() {
  const passwordInput = document.getElementById('js-password');
  const passwordValidator = new InputValidator(passwordInput);
  passwordInput.addEventListener('change', function() {
    passwordValidator.validate(InputValidator.password, InputValidator.defaultMessages.password);
  });

  const form = document.getElementById('js-new-customer-form');
  form.addEventListener('submit', function(event) {
    event.preventDefault();
    const isPasswordValid = !passwordInput.value || passwordValidator.validate(InputValidator.password, InputValidator.defaultMessages.password);

    if (isPasswordValid) {
      loadingMask.show();
      registerMarketplace(this);
    } else {
      InputValidator.focusFirstInvalid(form);
    }
  });
}

/**
 * Marketplace: initialize 6-digit code input behavior (numeric-only, auto-advance, paste support).
 */
function initMarketplaceCodeForm() {
  const form = document.getElementById('js-marketplace-code-form');
  if (!form) {
    return;
  }

  const hidden = document.getElementById('js-code-hidden');
  const inputs = Array.from(form.querySelectorAll('.js_code_box'));

  const setHidden = () => {
    hidden.value = inputs.map(i => (i.value || '').replace(/\D/g, '')).join('').slice(0, 6);
  };

  const focusNext = (idx) => {
    if (idx < inputs.length - 1) inputs[idx + 1].focus();
  };

  const focusPrev = (idx) => {
    if (idx > 0) inputs[idx - 1].focus();
  };

  inputs.forEach((input, idx) => {
    input.addEventListener('input', () => {
      const v = input.value.replace(/\D/g, '');
      if (v.length > 1) {
        input.value = v.charAt(0);
        let rest = v.slice(1);
        let j = idx + 1;
        while (rest && j < inputs.length) {
          inputs[j].value = rest.charAt(0);
          rest = rest.slice(1);
          j++;
        }
        if (j - 1 < inputs.length) focusNext(Math.max(j - 2, idx));
      } else {
        input.value = v;
        if (v !== '') focusNext(idx);
      }
      setHidden();
    });

    input.addEventListener('keydown', (e) => {
      if (e.key === 'Backspace' && input.value === '') {
        const prev = inputs[idx - 1];
        if (prev) {
          focusPrev(idx);
          prev.value = '';
        }
        setHidden();
      }
    });

    input.addEventListener('paste', (e) => {
      e.preventDefault();
      const text = (e.clipboardData || window.clipboardData).getData('text');
      const digits = (text || '').replace(/\D/g, '').slice(0, 6);
      for (let i = 0; i < inputs.length; i++) {
        inputs[i].value = digits.charAt(i) || '';
      }
      setHidden();
      const nextIdx = Math.min(digits.length, inputs.length - 1);
      inputs[nextIdx].focus();
    });
  });

  form.addEventListener('submit', (e) => {
    e.preventDefault();
    hideErrorMessages();
    setHidden();
    if (!/^\d{6}$/.test(hidden.value)) {
      setErrorMessage(form, 'Bitte gib den 6-stelligen Code ein.');
      inputs[0]?.focus();
      return;
    }

    loadingMask.show();
    const fd = new FormData(form);
    fd.append('code', hidden.value);

    doFetch('/loginActionsEndpoint.php?action=marketplaceVerifyCode', {
      method: 'POST',
      body: fd,
    }, (response) => {
      if (response?.success && response?.data?.html) {
        showResponseHtml(response.data);
      } else {
        setErrorMessage(form, response.error || DEFAULT_ERROR);
      }
      loadingMask.hide();
    });
  });
}

/**
 * Marketplace: attach handler to send-sms form if present
 */
function addMarketplaceSendSmsListener() {
  const form = document.getElementById('js-marketplace-sms-form');
  if (!form) {
    return;
  }
  // Ensure the button is enabled when the view is rendered.
  const initBtn = form.querySelector('button[type="submit"]');
  if (initBtn) {
    initBtn.disabled = false;
  }
  form.addEventListener('submit', (e) => {
    e.preventDefault();
    hideErrorMessages();

    const btn = form.querySelector('button[type="submit"]');
    if (btn) {
      btn.disabled = true;
    }
    loadingMask.show();

    const fd = new FormData(form);
    doFetch('/loginActionsEndpoint.php?action=marketplaceSendSms', {
      method: 'POST',
      body: fd,
    }, (response) => {
      if (response.success && response.data && response.data.html) {
        showResponseHtml(response.data);
      } else {
        setErrorMessage(form, response.error || DEFAULT_ERROR);
        if (btn) btn.disabled = false;
      }
      loadingMask.hide();
    });
  });
}

function registerNewCustomer(form) {
  fetchWithCaptcha(
      form,
      '/loginActionsEndpoint.php?action=registerNew',
      (response) => {
        window.location.href = response.data.target;
      },
      (response) => {
        setErrorMessage(form, response.error);
        loadingMask.hide();
      },
      'register'
  );
}

function registerMarketplace(form) {
  fetchWithCaptcha(
      form,
      '/loginActionsEndpoint.php?action=registerMarketplace',
      (response) => {
        window.location.href = response.data.target;
      },
      (response) => {
        setErrorMessage(form, response.error);
        loadingMask.hide();
      },
      'register'
  );
}
